import { z } from 'zod';
import { nullableDate, nullableText, optionalDate, optionalText } from '../common.js';
import { sortParam } from '../sort.js';
/**
 * Verträge des Moduls „Mitarbeiter“ (KON-075 bis KON-079). Ein Mitarbeiter ist ein eigener Datensatz und muss kein Benutzer sein. Ein Benutzer
 * kann mit einem Mitarbeiter verknüpft sein. Die Felder in SENSITIVE_FIELDS sind nach DSGVO besonders schützenswert. Sie gehören zu „Lohn und Abrechnung": Die API liefert
 * sie nur auf ausdrücklichen Abruf (`POST /employees/:id/reveal`, Recht `hr.payroll`), schreibt das Anzeigen ins Protokoll und nimmt Änderungen nur mit den Lohndaten an.
 */
/** Felder, die standardmäßig verborgen sind. Die normale Antwort sagt nur, ob ein Wert hinterlegt ist. */
export const SENSITIVE_FIELDS = ['birthDate', 'taxNumber', 'taxId', 'socialSecurityNumber', 'iban', 'bic'];
export const SENSITIVE_LABELS = {
    birthDate: 'Geburtsdatum',
    taxNumber: 'Steuernummer',
    taxId: 'Steuer-ID',
    socialSecurityNumber: 'Sozialversicherungsnummer',
    iban: 'IBAN',
    bic: 'BIC',
};
/** Bezeichnungen der übrigen Felder, für den Verlauf und Fehlermeldungen */
export const EMPLOYEE_FIELD_LABELS = {
    personnelNumber: 'Personalnummer',
    firstName: 'Vorname',
    lastName: 'Nachname',
    position: 'Position',
    department: 'Abteilung',
    email: 'E-Mail',
    phone: 'Telefon',
    street: 'Straße',
    postalCode: 'PLZ',
    city: 'Ort',
    emergencyName: 'Notfallkontakt',
    emergencyPhone: 'Telefon des Notfallkontakts',
    hiredOn: 'Eintritt',
    leftOn: 'Austritt',
    vacationDaysPerYear: 'Urlaubsanspruch',
    weeklyHours: 'Wochenstunden',
    userId: 'Benutzerzugang',
    ...SENSITIVE_LABELS,
};
const name = (label) => z.string().trim().min(1, `${label} fehlt`).max(80);
/** Urlaubsanspruch in Tagen je Jahr, halbe Tage erlaubt (KON-120) */
const vacationDays = z
    .number('Bitte eine Zahl angeben')
    .min(0, 'Der Urlaubsanspruch darf nicht negativ sein')
    .max(366, 'Höchstens 366 Tage')
    .refine((v) => Number.isInteger(v * 2), 'Nur ganze oder halbe Tage, zum Beispiel 27,5');
/** Vertragliche Wochenstunden (KON-040), Viertelstunden genau */
const weeklyHours = z
    .number('Bitte eine Zahl angeben')
    .positive('Mehr als 0 Stunden')
    .max(60, 'Höchstens 60 Stunden')
    .refine((v) => Number.isInteger(v * 4), 'Auf die Viertelstunde genau, zum Beispiel 37,5');
const email = z
    .string()
    .trim()
    .max(200)
    .optional()
    .transform((v) => (v === '' ? undefined : v))
    .pipe(z.email('Ungültige E-Mail-Adresse').optional());
const nullableEmail = z
    .string()
    .trim()
    .max(200)
    .nullable()
    .optional()
    .transform((v) => (v === '' ? null : v))
    .pipe(z.email('Ungültige E-Mail-Adresse').nullable().optional());
/**
 * IBAN ohne Leerzeichen in Großbuchstaben. Geprüft wird die Form, nicht die Prüfziffer. Die Meldung sagt, was nicht stimmt und wie es aussehen soll (KON-083).
 */
export const ibanSchema = z
    .string()
    .trim()
    .transform((v) => v.replace(/\s+/g, '').toUpperCase())
    .pipe(z.string().superRefine((v, ctx) => {
    const say = (message) => ctx.addIssue({ code: 'custom', message });
    if (!/^[A-Z0-9]+$/.test(v))
        return say('Eine IBAN enthält nur Buchstaben und Ziffern, Leerzeichen sind erlaubt.');
    if (!/^[A-Z]{2}/.test(v))
        return say('Eine IBAN beginnt mit dem Länderkürzel aus zwei Buchstaben, zum Beispiel DE.');
    if (!/^\d{2}/.test(v.slice(2)))
        return say('Nach dem Länderkürzel folgen zwei Prüfziffern, zum Beispiel DE89.');
    if (v.startsWith('DE') && v.length !== 22)
        return say(`Eine deutsche IBAN hat 22 Zeichen (DE und 20 Ziffern). Ihre Eingabe hat ${v.length}.`);
    if (v.length < 15 || v.length > 34)
        return say(`Eine IBAN hat 15 bis 34 Zeichen. Ihre Eingabe hat ${v.length}.`);
}));
export const bicSchema = z
    .string()
    .trim()
    .transform((v) => v.replace(/\s+/g, '').toUpperCase())
    .pipe(z.string().superRefine((v, ctx) => {
    const say = (message) => ctx.addIssue({ code: 'custom', message });
    if (v.length !== 8 && v.length !== 11)
        return say(`Eine BIC hat 8 oder 11 Zeichen, zum Beispiel COBADEFF oder COBADEFFXXX. Ihre Eingabe hat ${v.length}.`);
    if (!/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(v))
        return say('Eine BIC besteht aus sechs Buchstaben (Bank und Land), zwei Zeichen für den Ort und optional drei für die Filiale, zum Beispiel COBADEFFXXX.');
}));
/** Wie nullableText, aber mit Prüfung: leer löscht den Wert */
export const nullableChecked = (schema) => z
    .string()
    .trim()
    .nullable()
    .optional()
    .transform((v) => (v === '' ? null : v))
    .pipe(z.union([schema, z.null(), z.undefined()]));
const optionalChecked = (schema) => z
    .string()
    .trim()
    .optional()
    .transform((v) => (v === '' ? undefined : v))
    .pipe(z.union([schema, z.undefined()]));
export const createEmployeeInput = z.object({
    /** Ohne Angabe vergibt Wirkia die nächste freie Nummer */
    personnelNumber: optionalText(20),
    firstName: name('Vorname'),
    lastName: name('Nachname'),
    position: optionalText(100),
    department: optionalText(100),
    email,
    phone: optionalText(40),
    street: optionalText(120),
    postalCode: optionalText(10),
    city: optionalText(80),
    emergencyName: optionalText(120),
    emergencyPhone: optionalText(40),
    hiredOn: optionalDate,
    leftOn: optionalDate,
    vacationDaysPerYear: vacationDays.optional(),
    weeklyHours: weeklyHours.optional(),
    /** Benutzerzugang, mit dem der Mitarbeiter verknüpft ist (freiwillig) */
    userId: z.uuid('Ungültiger Benutzer').optional(),
});
export const updateEmployeeInput = z
    .object({
    personnelNumber: z.string().trim().min(1, 'Personalnummer fehlt').max(20).optional(),
    firstName: name('Vorname').optional(),
    lastName: name('Nachname').optional(),
    position: nullableText(100),
    department: nullableText(100),
    email: nullableEmail,
    phone: nullableText(40),
    street: nullableText(120),
    postalCode: nullableText(10),
    city: nullableText(80),
    emergencyName: nullableText(120),
    emergencyPhone: nullableText(40),
    hiredOn: nullableDate,
    leftOn: nullableDate,
    vacationDaysPerYear: vacationDays.nullable().optional(),
    weeklyHours: weeklyHours.nullable().optional(),
    userId: z.uuid('Ungültiger Benutzer').nullable().optional(),
    /** false deaktiviert den Mitarbeiter (KON-079), true aktiviert ihn wieder */
    isActive: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const listEmployeesQuery = z.object({
    q: z.string().trim().max(100).optional(),
    /** Vorgabe: alle. Mit „active“ und „inactive“ eingeschränkt. */
    status: z.enum(['active', 'inactive']).optional(),
    department: z.string().trim().max(100).optional(),
    /** Sortierung per Klick auf die Spalte, Vorgabe nach Namen (Nachname, Vorname) */
    sort: sortParam(['name', 'personnelNumber', 'position', 'department', 'status'], 'name'),
});
export const EMPLOYEE_FILE_KINDS = ['contract', 'amendment', 'other'];
export const EMPLOYEE_FILE_KIND_LABELS = {
    contract: 'Arbeitsvertrag',
    amendment: 'Zusatzvereinbarung',
    other: 'Sonstiges',
};
/** Höchstzahl der Dateien je Mitarbeiter */
export const EMPLOYEE_FILE_LIMIT = 20;
//# sourceMappingURL=employees.js.map